/** Roles an adviser can hold within a firm. */
export const AdviserRole = {
    Owner: 'owner',
    Adviser: 'adviser',
    Paraplanner: 'paraplanner',
    ReadOnly: 'read_only',
};
/** Commercial plan tiers (firm sizes 1–10 advisers). See packages/config. */
export const PlanTier = {
    Starter: 'starter',
    Practice: 'practice',
    Firm: 'firm',
};
/** Cadence for recurring cash-flows (used later by the projection engine). */
export const Frequency = {
    Once: 'once',
    Monthly: 'monthly',
    Quarterly: 'quarterly',
    Annually: 'annually',
};
/** Lifecycle status of a client record. Mirrors the `clients.status` CHECK. */
export const ClientStatus = {
    Active: 'active',
    Archived: 'archived',
};
/**
 * Tax wrapper / holding structure for an asset. Drives per-wrapper growth
 * assumptions and (later) per-wrapper illustrative tax treatment.
 */
export const Wrapper = {
    Cash: 'cash',
    CashIsa: 'cash_isa',
    StocksIsa: 'stocks_isa',
    Lisa: 'lisa',
    Jisa: 'jisa',
    Nsi: 'nsi',
    Gia: 'gia',
    DcPension: 'dc_pension',
    DbPension: 'db_pension',
    StatePension: 'state_pension',
    Property: 'property',
    Business: 'business',
    OtherAsset: 'other_asset',
};
/** Every wrapper, in canonical display order. */
export const WRAPPER_VALUES = [
    Wrapper.Cash,
    Wrapper.CashIsa,
    Wrapper.StocksIsa,
    Wrapper.Lisa,
    Wrapper.Jisa,
    Wrapper.Nsi,
    Wrapper.Gia,
    Wrapper.DcPension,
    Wrapper.DbPension,
    Wrapper.StatePension,
    Wrapper.Property,
    Wrapper.Business,
    Wrapper.OtherAsset,
];
/** Human-readable label per wrapper (UI + report copy). */
export const WRAPPER_LABELS = {
    cash: 'Cash',
    cash_isa: 'Cash ISA',
    stocks_isa: 'Stocks & Shares ISA',
    lisa: 'Lifetime ISA',
    jisa: 'Junior ISA',
    nsi: 'NS&I / Premium Bonds',
    gia: 'General Investment Account',
    dc_pension: 'DC Pension',
    db_pension: 'DB Pension',
    state_pension: 'State Pension',
    property: 'Property',
    business: 'Business interest',
    other_asset: 'Other asset',
};
/**
 * The same label, for the places that hold a wrapper as a plain string rather
 * than as a `Wrapper` — engine warning messages, validation findings, imported
 * rows still being checked.
 *
 * It exists because those were the places that leaked. An adviser reading the
 * modelling notes was told a row was a `state_pension` or a `dc_pension`, in
 * the contract's own snake_case, in the middle of an otherwise plain-English
 * sentence — a wire key printed where a product name belonged (round 8, C6).
 *
 * Falls back to the raw key rather than throwing: a message that names an
 * unrecognised wrapper awkwardly is still a message, and a projection must
 * never fail because a label table is behind the contract.
 */
export function wrapperLabel(wrapper) {
    return WRAPPER_LABELS[wrapper] ?? wrapper;
}
/**
 * Wrappers that carry an ongoing charge an adviser can override per holding.
 *
 * A property, a cash account, an NS&I holding or a DB/State Pension promise has
 * no fee drag, so a "Fees" control on one of those can only ever be answered
 * "not applicable". Lives here rather than beside either screen because BOTH
 * the fact-find builder and the import review panel offer the same override on
 * the same holdings, and `setAssumption` has to refuse it on the others — three
 * copies of this list is how they end up disagreeing about which assets have
 * fees.
 */
export const FEE_BEARING_WRAPPERS = [
    'stocks_isa',
    'lisa',
    'gia',
    'dc_pension',
];
/** True when a holding in this wrapper can carry an adviser-set fee drag. */
export function wrapperCarriesFees(wrapper) {
    return FEE_BEARING_WRAPPERS.includes(wrapper);
}
/** Kind of a recurring inflow stream. */
export const IncomeKind = {
    Salary: 'salary',
    SelfEmployment: 'self_employment',
    Rental: 'rental',
    PensionIncome: 'pension_income',
    Other: 'other',
};
/** Kind of a spending line. */
export const SpendingKind = {
    Essential: 'essential',
    Lifestyle: 'lifestyle',
    OneOff: 'one_off',
};
/** Kind of an outstanding liability. */
export const LiabilityKind = {
    Mortgage: 'mortgage',
    Loan: 'loan',
    Other: 'other',
};
/** Kind of a modelled one-off life event. */
export const LifeEventKind = {
    HousePurchase: 'house_purchase',
    HouseSale: 'house_sale',
    Inheritance: 'inheritance',
    GiftOut: 'gift_out',
    Education: 'education',
    OneOffExpense: 'one_off_expense',
    OneOffReceipt: 'one_off_receipt',
    Other: 'other',
};
/** Cash direction of a life event relative to the household. */
export const LifeEventDirection = {
    Inflow: 'inflow',
    Outflow: 'outflow',
    None: 'none',
};
// --- Fact-find (DB-aligned) enums -------------------------------------------
// These mirror the CHECK constraints in packages/db/migrations/0005 exactly, so
// a contract-validated payload can be inserted without any per-route mapping.
// They are deliberately separate from the web plan enums above (IncomeKind /
// SpendingKind / LiabilityKind), which describe the fixture-driven web model.
/** Who holds a fact-find item. Mirrors the `owner` CHECK on 0005 tables. */
export const HoldingOwner = {
    Client: 'client',
    Partner: 'partner',
    Joint: 'joint',
};
/** Asset holding type. Mirrors `assets.type` in 0005. */
export const AssetType = {
    Cash: 'cash',
    CashIsa: 'cash_isa',
    StocksIsa: 'stocks_isa',
    Gia: 'gia',
    DcPension: 'dc_pension',
    DbPension: 'db_pension',
    Property: 'property',
    Other: 'other',
};
/** Income stream type. Mirrors `income_streams.type` in 0005. */
export const IncomeStreamType = {
    Employment: 'employment',
    SelfEmployment: 'self_employment',
    DcPension: 'dc_pension',
    DbPension: 'db_pension',
    StatePension: 'state_pension',
    Rental: 'rental',
    Dividend: 'dividend',
    SavingsInterest: 'savings_interest',
    Other: 'other',
};
/** Spending category. Mirrors `spending_items.category` in 0005. */
export const SpendingCategory = {
    Essential: 'essential',
    Lifestyle: 'lifestyle',
    Luxury: 'luxury',
    OneOff: 'one_off',
};
/** Liability type. Mirrors `liabilities.type` in 0005. */
export const LiabilityType = {
    Mortgage: 'mortgage',
    Loan: 'loan',
    CreditCard: 'credit_card',
    Other: 'other',
};
/** Scenario lifecycle status. Mirrors `scenarios.status` in 0004. */
export const ScenarioStatus = {
    Draft: 'draft',
    Active: 'active',
    Archived: 'archived',
};
/**
 * Taxpayer residency region. Phase 1 supports **rest-of-UK only** (`rUK` =
 * England, Wales, Northern Ireland).
 *
 * Scotland is deliberately NOT a member of this type: there is no valid domain
 * value for a Scottish taxpayer, so no product code can construct one and no
 * saved plan can hold one.
 *
 * Removing the value does not make the boundary silent — that would be the
 * failure mode. It stays enforced and stated in three independent places, and
 * removing any one of them re-opens the risk of a Scottish client being modelled
 * at rest-of-UK rates without anyone noticing:
 *
 * 1. `taxRegionSchema` refuses the literal string `'scotland'` on the write path
 *    with an explanatory 422, not a generic "invalid value".
 * 2. The DB enforces `check (tax_region = 'rUK')`.
 * 3. The fact-find parser raises a `region_tax` ambiguity on Scottish cues, and
 *    every plan states its rest-of-UK basis on screen and in the report.
 */
export const TaxRegion = {
    RestOfUk: 'rUK',
};
/**
 * The rest-of-UK basis, stated rather than selected (point 3 above).
 *
 * One constant so the app and the PDF cannot drift, and so the basis cannot be
 * deleted from the UI without deleting it from the report too.
 *
 * Phrased as the countries it DOES cover (feedback 2026-08-11). It used to
 * read "Scottish taxpayer rates are not yet supported", which put a caveat
 * about a taxpayer the product refuses to model on every client-facing
 * illustration — including the overwhelming majority prepared for clients in
 * England, Wales and Northern Ireland, for whom it described a limitation that
 * could never apply to them.
 *
 * The second sentence stays explicit rather than leaving Scotland to be
 * noticed missing from a list. The gate exists in three independent places —
 * the `tax_region` DB CHECK, `taxRegionSchema` here in packages/shared, and
 * `assertSupportedTaxRegion` in packages/tax — but each fires only on a
 * payload that DECLARES a Scottish taxpayer.
 *
 * Two adviser-facing prompts now exist as well, and neither is the gate:
 * People shows the tax residence with Scotland named and unselectable, and the
 * importer holds the commit when a note carries a Scottish cue until the
 * adviser confirms the client is not a Scottish taxpayer. **Neither persists a
 * Scottish answer** — there is no domain value to store one in — so a Scottish
 * status cannot be recorded, the adviser's confirmation is not kept, and a
 * colleague opening the same client later sees no warning. A caller that is
 * not this UI (a direct write of the same document) meets none of the prompts.
 *
 * So this sentence remains the report's only standing defence for a reader in
 * Scotland (review finding, 2026-08-12): a layperson must be told outright,
 * not by omission, that these are not their rates.
 */
export const TAX_BASIS_STATEMENT = 'Modelled on the income tax rates for England, Wales and Northern Ireland. Scottish income tax rates differ and are not modelled.';
//# sourceMappingURL=domain.js.map